/**
 * Labstep
 *
 * @module prosemirror/components/Menu/Commands/Modal/Add
 * @desc A modal to add elements
 */

import ExperimentWorkflowActionAddProtocol from 'labstep-web/components/ExperimentWorkflow/Action/AddProtocol';
import { ExperimentWorkflowActionLinkExperimentWorkflowContainer as ExperimentWorkflowActionLinkExperimentWorkflow } from 'labstep-web/components/ExperimentWorkflow/Action/LinkExperimentWorkflow';
import FileActionCreate from 'labstep-web/components/File/Action/Create';
import { ProtocolDeviceActionCreateContainer as ProtocolDeviceActionCreate } from 'labstep-web/components/ProtocolDevice/Action/Create';
import { ProtocolTimerActionCreateForm } from 'labstep-web/components/ProtocolTimer/Action/Create/Form';
import { allowedVideoExtensions } from 'labstep-web/constants/mime-types';
import { SLASH_TRIGGER } from 'labstep-web/constants/prosemirror';
import { CoreCodeMirrorModal as CodeMirror } from 'labstep-web/core/CodeMirror/Modal';
import { FormReusable } from 'labstep-web/core/Form/Reusable';
import { ModalDefaultContainer as ModalDefault } from 'labstep-web/core/Modal/Default';
import { PremiumFeatureModal } from 'labstep-web/core/PremiumFeature/Modal';
import type { APIResponse } from 'labstep-web/models';
import type { File } from 'labstep-web/models/file.model';
import { ProtocolDevice } from 'labstep-web/models/protocol-device.model';
import { MenuCommandsModalHtmlTable } from 'labstep-web/prosemirror/components/Menu/Commands/Modal/HtmlTable';
import { handleSubmitElement } from 'labstep-web/prosemirror/extensions/slash/commands';
import { replaceWithCode } from 'labstep-web/prosemirror/marks/code/commands';
import { fields } from 'labstep-web/prosemirror/marks/link';
import { replaceWithLink } from 'labstep-web/prosemirror/marks/link/commands';
import {
  replaceWithMultipleNodes,
  replaceWithNode,
} from 'labstep-web/prosemirror/nodes/commands';
import { replaceWithReference } from 'labstep-web/prosemirror/nodes/reference/commands';
import { replaceWithTable } from 'labstep-web/prosemirror/nodes/table/commands';
import { getToken } from 'labstep-web/prosemirror/utils/selection';
import { getIdAttribute } from 'labstep-web/services/schema/helpers';
import { selectFilesInProgress } from 'labstep-web/state/selectors/file';
import store from 'labstep-web/state/store';
import type React from 'react';
import type { IMenuCommandsModalAddProps } from './types';
import { updateFile } from './utils';

export const MenuCommandsModalAdd: React.FC<
  IMenuCommandsModalAddProps
> = ({
  state,
  dispatch,
  view,
  entity,
  experimentWorkflow,
  submittedElement,
}) => {
  if (!submittedElement) {
    return null;
  }

  const defaultProps = {
    modalProps: {
      open: true,
      onClose: () => handleSubmitElement(state, dispatch, -1),
      viewComponent: () => null,
    },
  };

  if (
    submittedElement.premiumFeature &&
    !submittedElement.premiumFeatureEnabled
  ) {
    return (
      <PremiumFeatureModal
        premiumFeature={submittedElement.premiumFeature}
        {...defaultProps.modalProps}
      />
    );
  }
  const token = getToken(state, SLASH_TRIGGER);
  const onSuccess = ({ response }: { response: APIResponse }) => {
    const nodeKey = submittedElement.key;
    const id = Array.isArray(response.result)
      ? response.result[0]
      : response.result;

    if (submittedElement.key) {
      replaceWithNode(
        state,
        dispatch,
        (token as any).from,
        (token as any).to,
        nodeKey,
        { [getIdAttribute(nodeKey)]: id },
        null as any,
      );
    }
    view.focus();
  };

  let fileIds: number[] = [];
  let files: File[] = [];
  const onFileUploadSuccess = ({
    response,
  }: {
    response: APIResponse;
  }) => {
    const id = Array.isArray(response.result)
      ? response.result[0]
      : response.result;
    const file: File = (response as any).entities.file[id];

    fileIds.push(id as number);
    files.push(file);
    const filesInProgress = selectFilesInProgress(
      store.getState(),
    ).length;

    if (submittedElement.key && filesInProgress <= 0) {
      replaceWithMultipleNodes(
        state,
        dispatch,
        (token as any).from,
        (token as any).to,
        submittedElement.key,
        fileIds,
        files,
      );
      fileIds = [];
      files = [];
    }
    view.focus();
  };

  let modal = null;

  switch (submittedElement.id) {
    case 'experiment_workflow_link':
      modal = (
        <ExperimentWorkflowActionLinkExperimentWorkflow
          experimentWorkflow={experimentWorkflow}
          options={{ onSuccess } as any}
          {...defaultProps}
        />
      );
      break;
    case 'protocol_collection':
      modal = (
        <ExperimentWorkflowActionAddProtocol
          experimentWorkflow={experimentWorkflow}
          onSuccess={onSuccess as any}
          isModal
          {...defaultProps}
        />
      );
      break;
    case 'protocol_timer':
      modal = (
        <ProtocolTimerActionCreateForm
          protocol={entity}
          options={{ onSuccess } as any}
          {...defaultProps}
        />
      );
      break;
    case 'file':
    case 'image': {
      modal = (
        <FileActionCreate
          entityType={entity.entityName}
          entityId={entity.id}
          options={
            { onSuccess: onFileUploadSuccess, toast: true } as any
          }
          onFolderWatcherSelect={({ file }) =>
            updateFile(entity, file, { onSuccess } as any)
          }
          {...defaultProps}
        />
      );
      break;
    }
    case 'video': {
      modal = (
        <FileActionCreate
          entityType={entity.entityName}
          entityId={entity.id}
          showOnly="device"
          accept={allowedVideoExtensions}
          options={
            { onSuccess: onFileUploadSuccess, toast: true } as any
          }
          onFolderWatcherSelect={({ file }) =>
            updateFile(entity, file, { onSuccess } as any)
          }
          {...defaultProps}
        />
      );
      break;
    }
    case 'link': {
      const onSubmit = ({ url, text }: any) => {
        replaceWithLink(
          state,
          dispatch,
          (token as any).from,
          (token as any).to,
          url,
          text,
        );
        view.focus();
      };
      modal = (
        <ModalDefault
          header="Add Link"
          content={() => (
            <FormReusable
              onSubmit={onSubmit}
              fields={fields}
              autoFocus
            />
          )}
          {...defaultProps.modalProps}
        />
      );
      break;
    }
    case 'code': {
      const onSubmit = ({ type, description }: any) => {
        replaceWithCode(
          state,
          dispatch,
          (token as any).from,
          (token as any).to,
          type,
          description,
        );
        view.focus();
      };
      modal = (
        <CodeMirror
          status={null}
          onSubmit={onSubmit}
          {...defaultProps}
        />
      );
      break;
    }
    case 'html_table': {
      const onSubmit = ({ rows, columns }: any) =>
        replaceWithTable(
          state,
          dispatch,
          (token as any).from,
          (token as any).to,
          rows,
          columns,
        );
      modal = (
        <MenuCommandsModalHtmlTable
          onSubmit={onSubmit}
          modalProps={defaultProps.modalProps}
        />
      );
      break;
    }
    case 'protocol_device': {
      modal = (
        <ProtocolDeviceActionCreate
          parent={entity}
          options={{
            onSuccess: ({ response }) => {
              replaceWithReference(
                state,
                dispatch,
                (token as any).from,
                (token as any).to,
                {
                  id: response!.result,
                  entityName: ProtocolDevice.entityName,
                },
              );
            },
          }}
          {...defaultProps}
        />
      );
      break;
    }
    default:
      break;
  }

  return modal;
};
